import React, { useContext, useEffect, useRef, useState } from 'react'
import Tabs, { TabsProps } from 'rc-tabs'
import axios from 'axios'

// types
import {
	ConfigResponse,
	// LoyaltyConfigResponse,
	SalonDetail,
	SalonEmployeesResponse,
	SalonPageQuery,
	SalonPageSetQuery,
	SalonReviewsResponse,
	SalonServicesResponse,
	ServiceCardCallbackData,
	ServiceDetailData
} from '../../../../types/types'

// utils
import { getFirstLevelCategoriesNames, getGoogleMapsLink, getPhoneNumber } from '../../../../utils/helper'
import { RATING_ID } from '../../../../utils/enums'
import { AppContext } from '../../../../utils/appProvider'

// components
import OpeningHours from '../OpeningHours/OpeningHours'
import SalonAboutUsMobile from '../SalonAboutUsMobile/SalonAboutUsMobile'
import CtaButtons from '../CtaButtons/CtaButtons'
import SalonAboutUsDesktop from '../SalonAboutUs/SalonAboutUs'
import SalonContacts, { SALON_CONTACTS_HEADING_ID } from '../SalonContacts/SalonContacts'
import SalonDetailCard from '../SalonDetailCard/SalonDetailCard'
import SalonPageReviews from '../SalonPageReviews/SalonPageReviews'
import AddReviewModal from '../AddReviewModal/AddReviewModal'
import SalonPricelists from '../SalonPricelists/SalonPricelists'
import SalonServicesCollapse from '../SalonServicesCollapse/SalonServicesCollapse'
import SalonServicesTabs from '../SalonServicesTabs/SalonServicesTabs'
import ProfiSalonHeader from '../ProfiSalonHeader/ProfiSalonHeader'
import GalleryDesktop from '../GalleryDesktop/GalleryDesktop'
import GalleryMobile from '../GalleryMobile/GalleryMobile'
import SalonServiceDetail from '../../../../components/SalonServiceDetail/SalonServiceDetail'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// hooks
import useMessages from '../../../../hooks/useMessages'
import useSalonReviews from '../../../../hooks/useSalonReviews'

// styles
import * as SC from './ProfiSalonStyles'
import { DesktopOnly, MobileOnly } from '../../../../styles/helpers'
import { SALON_PAGE_MOBILE_BREAKPOINT_KEY } from '../../../../styles/constants'

type Props = {
	salonData: SalonDetail
	countriesData: ConfigResponse['rolloutCountries']
	salonServicesData: SalonServicesResponse
	salonReviewsData: SalonReviewsResponse
	employeesData: SalonEmployeesResponse | undefined
	// loyaltyConfigData: LoyaltyConfigResponse
	onOpenBookingModal: (data?: ServiceCardCallbackData) => void
	query: SalonPageQuery
	setQuery: SalonPageSetQuery
	fetchSalonData: () => Promise<void>
	allowedReservations: boolean
}

const ProfiSalon = (props: Props) => {
	const { messages } = useMessages()

	const {
		salonData,
		countriesData,
		salonServicesData,
		salonReviewsData,
		employeesData,
		onOpenBookingModal,
		query,
		setQuery,
		fetchSalonData
		// loyaltyConfigData
	} = props

	const { isAuthorized } = useContext(AppContext)
	const phoneNumber = getPhoneNumber(salonData.phones[0], countriesData)

	const [tabKey, setTabKey] = useState<string>(query.reservationID ? 'reviews' : 'overview')
	const [isAddReviewModalOpen, setIsAddReviewModalOpen] = useState(false)
	const [isAddReviewModalFocusTrapEnabled, setIsAddReviewModalFocusTrapEnabled] = useState(true)
	const [serviceDetail, setServiceDetail] = useState<ServiceDetailData | null>(null)

	const mobileReviewsRef = useRef<HTMLElement>(null)
	const desktopReviewsRef = useRef<HTMLElement>(null)

	const { reviews, reviewsPage, isLoadingReviews, fetchMoreReviews } = useSalonReviews(salonData.id, salonReviewsData)

	const renderServices = salonServicesData && salonServicesData.groupedServicesByCategory?.length > 0
	const renderPricelists = salonData.pricelists && salonData.pricelists.length > 0

	const salonServices = getFirstLevelCategoriesNames(salonServicesData)

	const googleMapLink = getGoogleMapsLink({ lat: salonData.address?.latitude, lon: salonData.address?.longitude })

	/** review is allowed to add
	 * - logged in user
	 * - user with a valid token in the URL (when guest user completes the reservation in the salon, an email with URL for providing a review for the reservation is sent to them. Shape of the URL should be: "salony/{seoSlugName}/?reservationID={reservationID}&t={token}")
	 */
	const isUserAllowedToSeeReviews = Boolean(isAuthorized || query.t)

	useEffect(() => {
		if (query.reservationID && isUserAllowedToSeeReviews) {
			// Both copies stay mounted (CSS hide); the hidden one's scrollIntoView is a no-op.
			mobileReviewsRef.current?.scrollIntoView()
			desktopReviewsRef.current?.scrollIntoView()
			// Email reservation links open this modal automatically, potentially at the same
			// time as the third-party cookie consent banner. Since the banner is outside our
			// modal, its controls would be blocked by the modal's focus trap.
			setIsAddReviewModalFocusTrapEnabled(false)
			setIsAddReviewModalOpen(true)
		}
	}, [query.reservationID, isUserAllowedToSeeReviews])

	const handleOpenAddReview = () => {
		setIsAddReviewModalFocusTrapEnabled(true)
		setIsAddReviewModalOpen(true)
	}

	const handleSubmitReviewSuccess = () => {
		setIsAddReviewModalOpen(false)
		fetchSalonData()
		fetchMoreReviews({ resetPage: true })
		setQuery({ ...query, reservationID: undefined, t: undefined })
	}

	const handleSubmitReviewError = (error: unknown) => {
		// if reservationID query parameter exists and user gets 409, it means, he has already posted a review for this salon with this reservationId
		if (query.reservationID && axios.isAxiosError(error) && error.response?.status === 409) {
			setIsAddReviewModalOpen(false)
			setQuery({ ...query, reservationID: undefined })
		}
	}

	const reviewsListProps = {
		salonData,
		reviewsData: salonReviewsData,
		query,
		onOpenAddReview: handleOpenAddReview,
		reviews,
		reviewsPage,
		isLoadingReviews,
		fetchMoreReviews
	}

	const getTabsItems = () => {
		const tabsItems: TabsProps['items'] = [
			{
				key: 'overview',
				label: messages.Overview,
				children: (
					<>
						{salonData.openingHoursData && (
							<SC.OpeningHoursWrapper>
								<OpeningHours openingHours={salonData.openingHoursData} />
							</SC.OpeningHoursWrapper>
						)}
						{renderServices && (
							<SC.ServicesWrapper>
								<SalonServicesCollapse
									phoneNumber={phoneNumber}
									services={salonServicesData}
									onServiceCardButtonClick={onOpenBookingModal}
									setServiceDetail={setServiceDetail}
								/>
							</SC.ServicesWrapper>
						)}
						<SC.AboutUsWrapper>
							<SalonAboutUsMobile
								salonData={salonData}
								countriesData={countriesData}
								employeesData={employeesData}
								shouldClampDescription={true}
							/>
						</SC.AboutUsWrapper>
					</>
				)
			}
		]

		tabsItems.push({
			key: 'reviews',
			label: messages.Reviews,
			children: (
				<SalonPageReviews {...reviewsListProps} ref={mobileReviewsRef} ratingComponentIdPrefix={RATING_ID.SALON_DETAIL_PAGE_REVIEWS_MOBILE_PREFIX} />
			)
		})

		tabsItems.push({
			key: 'about-us',
			label: messages['About us'],
			children: (
				<>
					<VisuallyHidden as='h2'>{messages['About us']}</VisuallyHidden>
					<SalonAboutUsMobile
						salonData={salonData}
						countriesData={countriesData}
						employeesData={employeesData}
						hideFirstSectionHeading
						hideTeamSection
					/>
				</>
			)
		})

		if (renderPricelists) {
			tabsItems.push({
				key: 'pricelists',
				label: messages['Price list'],
				children: (
					<>
						<VisuallyHidden as='h2'>{messages['Price list']}</VisuallyHidden>
						<SalonPricelists pricelists={salonData.pricelists} hideHeading />
					</>
				)
			})
		}

		return tabsItems
	}

	return (
		<div>
			{/* desktop header */}
			<DesktopOnly $breakpoint={SALON_PAGE_MOBILE_BREAKPOINT_KEY}>
				<ProfiSalonHeader
					name={salonData.name}
					logoUrl={salonData.logo?.resizedImages?.small}
					address={salonData.formattedAddress}
					googleMapLink={googleMapLink}
					ratingsData={salonData.ratingsData}
					openingHoursData={salonData.openingHoursData}
					ratingId={RATING_ID.SALON_DETAIL_HEADER_DESKTOP}
					// loyaltyConfigData={loyaltyConfigData}
				/>
			</DesktopOnly>

			{/* images */}
			<DesktopOnly $breakpoint={SALON_PAGE_MOBILE_BREAKPOINT_KEY}>
				<GalleryDesktop images={salonData.images} />
			</DesktopOnly>
			<MobileOnly $breakpoint={SALON_PAGE_MOBILE_BREAKPOINT_KEY}>
				<GalleryMobile images={salonData.images} />
			</MobileOnly>

			{/* mobile content */}
			<MobileOnly $breakpoint={SALON_PAGE_MOBILE_BREAKPOINT_KEY}>
				<ProfiSalonHeader
					name={salonData.name}
					address={salonData.formattedAddress}
					logoUrl={salonData.logo?.resizedImages?.small}
					ratingsData={salonData.ratingsData}
					ratingId={RATING_ID.SALON_DETAIL_HEADER_MOBILE}
					// loyaltyConfigData={loyaltyConfigData}
				/>
				<CtaButtons
					salonData={salonData}
					countriesData={countriesData}
					openReservationBookingModal={onOpenBookingModal}
					googleMapLink={googleMapLink}
				/>
				<SC.TabsContainer>
					<Tabs activeKey={tabKey} onChange={(activeKey) => setTabKey(activeKey)} items={getTabsItems()} animated={false} />
				</SC.TabsContainer>
			</MobileOnly>

			{/* desktop content */}
			<DesktopOnly $breakpoint={SALON_PAGE_MOBILE_BREAKPOINT_KEY}>
				<SC.Columns>
					<SC.MainColumn>
						{/* about us text */}
						{salonData.aboutUsFirst && <SC.AboutUsText>{salonData.aboutUsFirst}</SC.AboutUsText>}
						{/* cta buttons */}
						<CtaButtons
							salonData={salonData}
							countriesData={countriesData}
							openReservationBookingModal={onOpenBookingModal}
							googleMapLink={googleMapLink}
						/>
						{/* services */}
						{renderServices && (
							<SC.ServicesSection aria-labelledby='salon-services-heading'>
								<SC.ServicesHeading id='salon-services-heading'>{messages.Services}</SC.ServicesHeading>
								<SalonServicesTabs
									phoneNumber={phoneNumber}
									services={salonServicesData}
									onServiceCardButtonClick={onOpenBookingModal}
									setServiceDetail={setServiceDetail}
									selectedTabKey={query.fe_selectedIndustryID || undefined}
								/>
							</SC.ServicesSection>
						)}
						{/* about us section */}
						<SalonAboutUsDesktop salonData={salonData} employeesData={employeesData} />
						{/* pricelists */}
						{renderPricelists && <SalonPricelists pricelists={salonData.pricelists} />}
						{/* ratings */}
						<SalonPageReviews
							{...reviewsListProps}
							ref={desktopReviewsRef}
							ratingComponentIdPrefix={RATING_ID.SALON_DETAIL_PAGE_REVIEWS_DESKTOP_PREFIX}
						/>
					</SC.MainColumn>
					<SC.SideColumn aria-labelledby={SALON_CONTACTS_HEADING_ID}>
						<SalonContacts salonData={salonData} countriesData={countriesData} /* loyaltyConfigData={loyaltyConfigData} */ />
					</SC.SideColumn>
				</SC.Columns>
				<SalonDetailCard salonData={salonData} countries={countriesData} salonServices={salonServices} />
			</DesktopOnly>
			{serviceDetail && (
				<SalonServiceDetail
					onServiceCardButtonClick={onOpenBookingModal}
					phoneNumber={phoneNumber}
					serviceData={serviceDetail}
					onClose={() => setServiceDetail(null)}
				/>
			)}
			{isAddReviewModalOpen && isUserAllowedToSeeReviews && (
				<AddReviewModal
					isOpen
					withFocusTrap={isAddReviewModalFocusTrapEnabled}
					onClose={() => setIsAddReviewModalOpen(false)}
					salonID={salonData.id}
					onSubmitSuccess={handleSubmitReviewSuccess}
					onSubmitError={handleSubmitReviewError}
					reservationID={query.reservationID || undefined}
					t={query.t || undefined}
				/>
			)}
		</div>
	)
}

export default ProfiSalon
